// src/components/layout/DashboardLayout.jsx - current erx mobile

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { MobileMenuButton } from './MobileMenuButton';
import { Sidebar } from './Sidebar';
import { PWAInstallButton } from '../ui/PWAInstallButton';
import { ClockInOutButton } from '../time-tracking/ClockInOutButton';
import { TimeTrackingProvider } from '@/contexts/TimeTrackingContext';
import { FloatingActionButton } from '../ai/FloatingActionButton';
import { HelpProvider } from '@/contexts/HelpContext';
import { HelpSlideOver } from '@/components/help/HelpSlideOver';
import { SupportProvider } from '@/contexts/SupportContext';
import { SupportSlideOver } from '@/components/support/SupportSlideOver';
import { useFeatureFlags } from '@/contexts/FeatureContext';
import { useAuth0 } from '@auth0/auth0-react';
import OfflineStatusBar from '@/components/offline/OfflineStatusBar';
import OfflineSessionManager from '@/components/offline/OfflineSessionManager';
import OfflineRouteGuard from '@/components/offline/OfflineRouteGuard';
import { isOfflineModeEnabled } from '@/services/offline/featureFlag';
import { authService } from '@/services/auth';
import { useVeradigmStore } from '@/stores/veradigmStore';
import { apiService } from '@/services/api';
import { performLogout } from '@/utils/logoutCleanup';
import { RolePreviewBanner } from './RolePreviewBanner';
import { useUser } from '@/contexts/UserContext';
import { useCalendarRealtimeSync } from '@/hooks/useCalendarRealtimeSync';

export function DashboardLayout({ children }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const isFullscreen = location.pathname === '/appointments/calendar';
  const { mode, features, loading: featuresLoading, solutionsLoaded } = useFeatureFlags();
  const { user } = useUser();

  // App-level realtime sync: the org appointment channel outlives page
  // navigation, so events keep patching the calendar store no matter which
  // page is open — no event-loss gaps between Home/calendar/chart
  useCalendarRealtimeSync({ organizationId: user?.organization_id });

  useEffect(() => {
    const handleSidebarOpenRequest = () => setIsSidebarOpen(true);
    window.addEventListener('sidebar-open-request', handleSidebarOpenRequest);
    return () => window.removeEventListener('sidebar-open-request', handleSidebarOpenRequest);
  }, []);

  // Check if we should show the FAB
  const showFAB = !location.pathname.includes('/login') &&
    !location.pathname.includes('/onboarding') && 
    !location.pathname.includes('/messages') &&
    features.core_ehr; // Only show in multi_feature mode

  // Check if should show back button
  const showBackButton = !location.pathname.includes('/login') &&
    !location.pathname.includes('/onboarding') &&
    location.pathname !== '/' &&
    location.pathname !== '/patients'; // Don't show on main listing pages
  // Get app title based on mode (same logic as Sidebar)
  const getAppTitle = () => {
    if (featuresLoading) return 'Loading...';
    if (mode === 'prism_scribe') return 'PrismScribe';
    if (mode === 'multi_feature') return 'PrismEHR';
    if (mode === 'form_builder') return 'CuratoForm';
    if (mode === 'ready2exercise') return 'Ready2Exercise';
    if (mode === 'rehab_prescribed') return 'Rehab Prescribed';
    return 'eRx Mobile';
  };


  if (!solutionsLoaded || mode === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (mode === 'prism_scribe') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Minimal header with just logo and sign out */}
        <ScribeMinimalHeader />
        {/* Main content */}
        <main>{children}</main>
      </div>
    );
  }

  return (
    <HelpProvider>
    <SupportProvider>
    <TimeTrackingProvider>
    <div className="min-h-screen bg-gray-100">
      <RolePreviewBanner />
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-30 bg-white h-14 px-3 flex items-center justify-between shadow-sm print:hidden ${isFullscreen ? 'hidden' : ''}`}>
        {/* min-w-0 on the group and the link so the title truncates instead of
            pushing the right-hand buttons past the edge on narrow phones */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {showBackButton && (
            <button
              onClick={() => window.history.back()}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <Link to="/" className="text-xl font-bold text-primary truncate min-w-0">
            {getAppTitle()}
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <ClockInOutButton compact />
          <PWAInstallButton />
          <MobileMenuButton onClick={() => setIsSidebarOpen(true)} />
        </div>
      </div>

      {isOfflineModeEnabled() && (
        <>
          <OfflineStatusBar />
          <OfflineSessionManager />
        </>
      )}

      <div className={`flex min-h-screen ${isFullscreen ? 'pt-0' : 'pt-14'} lg:pt-0 print:min-h-0 print:pt-0 print:block`}>
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        {/* Main content */}
        {/* min-w-0: a row-flex item otherwise adopts its content's min-content
            width, so any over-wide child (e.g. the phone calendar header) made
            the whole page wider than the viewport and the window panned sideways.
            overflow-x-clip (not overflow-auto): this wrapper never scrolls (the
            window does), but an overflow:auto box captures position:sticky, which
            broke sticky headers on every non-fullscreen page. `clip` still
            contains wide content without creating a scrollport. */}
        <div className={`flex-1 min-w-0 overflow-x-clip ${isFullscreen ? 'flex flex-col' : ''} print:overflow-visible`}>
          <main className={isFullscreen ? 'flex-1 min-h-0' : 'container-mobile page-mobile'}>
            {isOfflineModeEnabled() ? <OfflineRouteGuard>{children}</OfflineRouteGuard> : children}
          </main>
        </div>
      </div>

      {/* Add the Floating Action Button */}
      {showFAB && !isFullscreen && <FloatingActionButton />}

      {/* Help slide-over panel */}
      <HelpSlideOver />

      {/* Support slide-over panel */}
      <SupportSlideOver />
    </div>
    </TimeTrackingProvider>
    </SupportProvider>
    </HelpProvider>
  );
}

const ScribeMinimalHeader = () => {
  const { logout } = useAuth0();
  const { clearUserConnection } = useVeradigmStore();
  const { canToggleScribeMode, toggleScribeMode } = useFeatureFlags();

  const handleLogout = async () => {
    authService.clearToken();
    clearUserConnection();
    apiService.clearVeradigmToken();
    await performLogout(logout);
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="max-w-2xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/prism-logo.png" alt="Prism" className="h-8 w-8" />
          <span className="font-semibold text-gray-900">PrismScribe</span>
        </div>
        <div className="flex items-center gap-4">
          {canToggleScribeMode && (
            <button
              onClick={toggleScribeMode}
              className="text-sm text-primary hover:text-primary/80"
            >
              Switch to full platform
            </button>
          )}
          <button
            onClick={handleLogout}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Sign Out
          </button>
        </div>
      </div>
    </header>
  );
};